/**
 * Общий стор параметров ипотечного флоу в localStorage.
 *
 * Все 4 экрана (welcome / offers / checkout / payment) читают и пишут сюда,
 * чтобы пользователь, переключаясь между вкладками stepper'а, видел те же
 * параметры (банк, город, остаток, ДР, выбранный оффер и т.п.).
 *
 * Хранится плоским JSON по одному ключу. Тип строгий — добавлять поля только
 * через расширение интерфейса.
 *
 * Когда у нас будет API сохранения параметров на бэке (a4314-save-ipoteka и
 * другие save-actions) — этот стор станет временным кэшем поверх API,
 * с автоматической инвалидацией при загрузке свежих данных по `ipoteka_id`.
 */

export interface IpotekaFormState {
    /** Тип страхования: property | life | combo | title и комбинации */
    productCode?: string;
    /** Подпись типа страхования из справочника (для отображения в summary) */
    productLabel?: string;
    /** Код банка-партнёра ипотеки */
    bank?: string;
    /** Подпись банка из справочника */
    bankLabel?: string;
    /** Тип недвижимости (код из API: Flat/House/...) */
    propertyType?: string;
    /** Подпись типа недвижимости из справочника */
    propertyLabel?: string;
    /** Город объекта (имя из dadata) */
    city?: string;
    /** Остаток основного долга по кредиту, ₽. СТРОКА — храним ровно как ввёл юзер,
     *  чтобы не терять копейки/хвостовой ноль («100.50» остаётся «100.50»). */
    balance?: string;
    /** Дата рождения заёмщика в ISO (YYYY-MM-DD) */
    birthIso?: string;
    /** Пол заёмщика */
    gender?: 'm' | 'f';
    /** Дата начала действия полиса в ISO */
    startIso?: string;
    /** Дополнительные опции (gas/wood/noDog/noOwn) */
    opts?: Record<string, boolean>;
    /** Выбранные категории покрытия (property/life/title) */
    coverage?: Record<string, boolean>;
    /** Композитный id выбранного оффера: `IpotekaPampadu-78` или `IpotekaInssmart-1`.
     *  Однозначно указывает на строку в нужной партнёрской таблице
     *  (`ipoteka_pampadu.offer` или `ipoteka_inssmart.offer`).
     */
    selectedOffer?: string;
    /** Партнёр выбранного оффера: 'IpotekaPampadu' | 'IpotekaInssmart'.
     *  Дублирует префикс selectedOffer — нужен чтобы не парсить id строкой везде. */
    selectedPartner?: string;
    /** Код СК (для UI/лого): 'amt', 'alfa', etc. (одинаковый ключ в COMPANY_META). */
    selectedCompanyCode?: string;
    /** Цена выбранного оффера, ₽ — для отображения в summary на checkout/payment */
    selectedPrice?: number;
    /** КВ выбранного оффера, ₽ */
    selectedKv?: number;
    /** Лого/название СК — для summary */
    selectedCompanyLabel?: string;
    selectedCompanyLogo?: string;
}

const KEY = 'ipoteka.formState';

export function getFormState(): IpotekaFormState {
    try {
        const raw = localStorage.getItem(KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw) as IpotekaFormState;
        // balance стал строкой; в старом localStorage мог лежать number — приводим,
        // иначе balance.trim()/regex в виджетах падают «trim is not a function».
        if (parsed.balance != null) parsed.balance = String(parsed.balance);
        // city тоже строковый и зовётся через .trim() — страхуемся от старых данных.
        if (parsed.city != null) parsed.city = String(parsed.city);
        return parsed;
    } catch {
        return {};
    }
}

export function setFormState(patch: Partial<IpotekaFormState>): void {
    try {
        const current = getFormState();
        const merged: IpotekaFormState = { ...current, ...patch };
        localStorage.setItem(KEY, JSON.stringify(merged));
    } catch {
        // ignore — если localStorage недоступен (приватный режим, квота)
    }
}

export function clearFormState(): void {
    try {
        localStorage.removeItem(KEY);
    } catch {
        // ignore
    }
}
